import React, { useState, useEffect, useCallback } from 'react';
import {
  FaGavel, FaSnowflake, FaLock, FaShieldAlt, FaPlus, FaTimes,
  FaHistory, FaFileAlt, FaSpinner, FaSearch, FaChevronDown,
  FaExclamationTriangle, FaCheckCircle
} from 'react-icons/fa';
import axiosInstance from '../../../../utils/axiosConfig';
import './CompliancePanel.css';

const PROTECTION_TYPES = {
  legal_hold: { label: 'Legal Hold', icon: FaGavel, cls: 'cp-hold' },
  freeze:     { label: 'Freeze', icon: FaSnowflake, cls: 'cp-freeze' },
  audit_lock: { label: 'Audit Lock', icon: FaLock, cls: 'cp-lock' },
};

const ENTITY_TYPES = [
  'property', 'user', 'inspection', 'incident_report', 'property_document',
  'anchor_point', 'cradle', 'davit_base', 'ladder', 'walkway',
  'handrail', 'static_line', 'fixed_equipment',
  'fire_alarm', 'sprinkler', 'emergency_light', 'fire_door',
  'fire_extinguisher', 'smoke_detector', 'heat_detector', 'job',
];

const CompliancePanel = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [protections, setProtections] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Apply protection form
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applyForm, setApplyForm] = useState({
    entityType: 'property', entityId: '', protectionType: 'legal_hold',
    reason: '', referenceNumber: '', notes: '',
  });
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Remove protection
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeReason, setRemoveReason] = useState('');
  const [removing, setRemoving] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/api/compliance/admin/dashboard');
      setDashboard(res.data);
    } catch (e) {
      setError('Failed to load compliance dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProtections = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/compliance/admin/protections');
      setProtections(res.data.protections || []);
    } catch (e) {
      console.error('Failed to fetch protections:', e);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/compliance/admin/history');
      setHistory(res.data.history || []);
    } catch (e) {
      console.error('Failed to fetch history:', e);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchProtections();
  }, [fetchDashboard, fetchProtections]);

  const handleApply = async (e) => {
    e.preventDefault();
    if (!applyForm.entityId || !applyForm.reason) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await axiosInstance.post('/api/compliance/admin/protect', applyForm);
      setApplyResult({ success: true, message: res.data.message });
      setApplyForm(prev => ({ ...prev, entityId: '', reason: '', referenceNumber: '', notes: '' }));
      fetchDashboard();
      fetchProtections();
    } catch (e) {
      setApplyResult({ success: false, message: e.response?.data?.error || 'Failed to apply protection' });
    } finally {
      setApplying(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget || !removeReason) return;
    setRemoving(true);
    try {
      await axiosInstance.post('/api/compliance/admin/unprotect', {
        protectionId: removeTarget.id,
        removalReason: removeReason,
      });
      setRemoveTarget(null);
      setRemoveReason('');
      fetchDashboard();
      fetchProtections();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to remove protection');
    } finally {
      setRemoving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="cp-loading">
        <FaSpinner className="cp-spinner" />
        <span>Loading compliance data...</span>
      </div>
    );
  }

  if (error) {
    return <div className="cp-error"><FaExclamationTriangle /> {error}</div>;
  }

  return (
    <div className="cp-container">
      <div className="cp-header">
        <div className="cp-title-row">
          <FaShieldAlt className="cp-title-icon" />
          <h2 className="cp-title">Compliance Protections</h2>
        </div>
        <p className="cp-subtitle">Manage legal holds, investigation freezes, and audit locks across all records.</p>
      </div>

      {/* Summary cards */}
      {dashboard && (
        <div className="cp-summary-grid">
          <div className="cp-summary-card cp-card-hold">
            <FaGavel className="cp-card-icon" />
            <div className="cp-card-body">
              <span className="cp-card-count">{dashboard.summary.legalHolds}</span>
              <span className="cp-card-label">Legal Holds</span>
            </div>
          </div>
          <div className="cp-summary-card cp-card-freeze">
            <FaSnowflake className="cp-card-icon" />
            <div className="cp-card-body">
              <span className="cp-card-count">{dashboard.summary.freezes}</span>
              <span className="cp-card-label">Frozen Records</span>
            </div>
          </div>
          <div className="cp-summary-card cp-card-lock">
            <FaLock className="cp-card-icon" />
            <div className="cp-card-body">
              <span className="cp-card-count">{dashboard.summary.auditLocks}</span>
              <span className="cp-card-label">Audit Locks</span>
            </div>
          </div>
          <div className="cp-summary-card cp-card-amend">
            <FaFileAlt className="cp-card-icon" />
            <div className="cp-card-body">
              <span className="cp-card-count">{dashboard.totalAmendments}</span>
              <span className="cp-card-label">Amendments</span>
            </div>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="cp-tabs" role="tablist">
        {[
          { key: 'dashboard', label: 'Active Protections', icon: FaShieldAlt },
          { key: 'apply', label: 'Apply Protection', icon: FaPlus },
          { key: 'history', label: 'History', icon: FaHistory },
          { key: 'retention', label: 'Retention Policies', icon: FaFileAlt },
        ].map(tab => (
          <button
            key={tab.key}
            className={`cp-tab ${activeTab === tab.key ? 'cp-tab-active' : ''}`}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              if (tab.key === 'history') fetchHistory();
            }}
          >
            <tab.icon className="cp-tab-icon" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Active protections list */}
      {activeTab === 'dashboard' && (
        <div className="cp-panel">
          {protections.length === 0 ? (
            <div className="cp-empty">
              <FaShieldAlt className="cp-empty-icon" />
              <p>No active protections</p>
              <button className="cp-btn cp-btn-primary" onClick={() => setActiveTab('apply')}>
                <FaPlus /> Apply Protection
              </button>
            </div>
          ) : (
            <div className="cp-protection-list">
              {protections.map(p => {
                const config = PROTECTION_TYPES[p.protection_type] || PROTECTION_TYPES.audit_lock;
                const Icon = config.icon;
                return (
                  <div key={p.id} className={`cp-protection-item ${config.cls}`}>
                    <div className="cp-prot-header">
                      <span className={`cp-prot-badge ${config.cls}`}>
                        <Icon /> {config.label}
                      </span>
                      <span className="cp-prot-entity">{p.entity_type} #{p.entity_id}</span>
                    </div>
                    <p className="cp-prot-reason">{p.reason}</p>
                    {p.reference_number && <p className="cp-prot-ref">Ref: {p.reference_number}</p>}
                    <div className="cp-prot-meta">
                      <span>Applied by {p.first_name} {p.last_name}</span>
                      <span>{formatDate(p.applied_at)}</span>
                      {p.expires_at && <span>Expires {formatDate(p.expires_at)}</span>}
                    </div>
                    <button
                      className="cp-btn cp-btn-remove"
                      onClick={() => { setRemoveTarget(p); setRemoveReason(''); }}
                    >
                      <FaTimes /> Remove
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Apply protection form */}
      {activeTab === 'apply' && (
        <div className="cp-panel">
          <form className="cp-apply-form" onSubmit={handleApply}>
            <div className="cp-form-row">
              <div className="cp-form-group">
                <label className="cp-form-label">Entity Type</label>
                <select
                  className="cp-select"
                  value={applyForm.entityType}
                  onChange={e => setApplyForm(prev => ({ ...prev, entityType: e.target.value }))}
                >
                  {ENTITY_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div className="cp-form-group">
                <label className="cp-form-label">Entity ID</label>
                <input
                  type="number"
                  className="cp-input"
                  placeholder="Record ID"
                  value={applyForm.entityId}
                  onChange={e => setApplyForm(prev => ({ ...prev, entityId: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="cp-form-group">
              <label className="cp-form-label">Protection Type</label>
              <div className="cp-radio-group">
                {Object.entries(PROTECTION_TYPES).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <label key={key} className={`cp-radio-card ${applyForm.protectionType === key ? 'cp-radio-selected' : ''}`}>
                      <input
                        type="radio"
                        name="protectionType"
                        value={key}
                        checked={applyForm.protectionType === key}
                        onChange={e => setApplyForm(prev => ({ ...prev, protectionType: e.target.value }))}
                      />
                      <Icon className="cp-radio-icon" />
                      <span>{config.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="cp-form-group">
              <label className="cp-form-label">Reason <span className="cp-required">*</span></label>
              <textarea
                className="cp-textarea"
                placeholder="Why is this protection being applied?"
                value={applyForm.reason}
                onChange={e => setApplyForm(prev => ({ ...prev, reason: e.target.value }))}
                required
                rows={3}
              />
            </div>

            <div className="cp-form-row">
              <div className="cp-form-group">
                <label className="cp-form-label">Reference Number</label>
                <input
                  type="text"
                  className="cp-input"
                  placeholder="e.g. Case #12345"
                  value={applyForm.referenceNumber}
                  onChange={e => setApplyForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                />
              </div>
              <div className="cp-form-group">
                <label className="cp-form-label">Notes</label>
                <input
                  type="text"
                  className="cp-input"
                  placeholder="Additional notes"
                  value={applyForm.notes}
                  onChange={e => setApplyForm(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>

            {applyResult && (
              <div className={`cp-result ${applyResult.success ? 'cp-result-success' : 'cp-result-error'}`}>
                {applyResult.success ? <FaCheckCircle /> : <FaExclamationTriangle />}
                <span>{applyResult.message}</span>
              </div>
            )}

            <button type="submit" className="cp-btn cp-btn-primary" disabled={applying}>
              {applying ? <FaSpinner className="cp-spinner" /> : <FaShieldAlt />}
              <span>{applying ? 'Applying...' : 'Apply Protection'}</span>
            </button>
          </form>
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && (
        <div className="cp-panel">
          {history.length === 0 ? (
            <div className="cp-empty"><FaHistory className="cp-empty-icon" /><p>No compliance history yet.</p></div>
          ) : (
            <div className="cp-history-list">
              {history.map(h => {
                const config = PROTECTION_TYPES[h.protection_type] || PROTECTION_TYPES.audit_lock;
                const Icon = config.icon;
                return (
                  <div key={h.id} className={`cp-history-item ${!h.is_active ? 'cp-history-removed' : ''}`}>
                    <div className="cp-hist-header">
                      <span className={`cp-prot-badge ${config.cls}`}><Icon /> {config.label}</span>
                      <span className="cp-prot-entity">{h.entity_type} #{h.entity_id}</span>
                      {!h.is_active && <span className="cp-hist-removed-badge">Removed</span>}
                    </div>
                    <p className="cp-prot-reason">{h.reason}</p>
                    <div className="cp-prot-meta">
                      <span>Applied by {h.applied_first} {h.applied_last} on {formatDate(h.applied_at)}</span>
                      {!h.is_active && h.removed_first && (
                        <span>Removed by {h.removed_first} {h.removed_last} on {formatDate(h.removed_at)}</span>
                      )}
                      {h.removal_reason && <span>Removal reason: {h.removal_reason}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Retention policies tab */}
      {activeTab === 'retention' && dashboard && (
        <div className="cp-panel">
          <div className="cp-retention-list">
            {(dashboard.retentionPolicies || []).map(p => (
              <div key={p.id} className="cp-retention-item">
                <div className="cp-ret-header">
                  <span className="cp-ret-entity">{p.entity_type.replace(/_/g, ' ')}</span>
                  <span className={`cp-ret-category cp-ret-${p.retention_category}`}>{p.retention_category}</span>
                </div>
                <div className="cp-ret-body">
                  <span className="cp-ret-years">{p.retention_years} year{p.retention_years !== 1 ? 's' : ''}</span>
                  {p.regulation_reference && <span className="cp-ret-reg">{p.regulation_reference}</span>}
                </div>
                {p.description && <p className="cp-ret-desc">{p.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remove confirmation modal */}
      {removeTarget && (
        <div className="cp-modal-overlay" onClick={() => setRemoveTarget(null)}>
          <div className="cp-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="cp-modal-title">
              <FaExclamationTriangle className="cp-modal-warn" /> Remove Protection
            </h3>
            <p className="cp-modal-desc">
              You are removing a <strong>{removeTarget.protection_type.replace('_', ' ')}</strong> from{' '}
              <strong>{removeTarget.entity_type} #{removeTarget.entity_id}</strong>.
            </p>
            <p className="cp-modal-desc">Original reason: {removeTarget.reason}</p>
            <div className="cp-form-group">
              <label className="cp-form-label">Reason for removal <span className="cp-required">*</span></label>
              <textarea
                className="cp-textarea"
                placeholder="Why is this protection being removed?"
                value={removeReason}
                onChange={e => setRemoveReason(e.target.value)}
                rows={3}
                autoFocus
              />
            </div>
            <div className="cp-modal-actions">
              <button className="cp-btn cp-btn-secondary" onClick={() => setRemoveTarget(null)}>Cancel</button>
              <button
                className="cp-btn cp-btn-danger"
                onClick={handleRemove}
                disabled={!removeReason.trim() || removing}
              >
                {removing ? <FaSpinner className="cp-spinner" /> : <FaTimes />}
                <span>{removing ? 'Removing...' : 'Remove Protection'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompliancePanel;
